import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ScrollView, View } from "react-native";
import {
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  CircleAlert,
  ClipboardList,
} from "lucide-react-native";
import {
  ActionBanner,
  ActionRowCard,
  Dialog,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  EMBER,
  DateField,
  MoonFormField,
  MoonTimeField,
  IconButton,
  TextField,
} from "@louise/moon-ui";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import type {
  PostProcedureTransitionCandidate,
  PostProcedureTransitionProcedure,
  PostProcedureTransitionStatus,
} from "@src/packages/cycles/presenters/postProcedureTransition";
import { useTranslations } from "@src/packages/i18n";

export type PostProcedureTransitionResult =
  | {
      status: "occurred";
      procedureDate: string;
      procedureTime: string | null;
      notes: string;
      embryosTransferred: number | null;
    }
  | {
      status: Exclude<PostProcedureTransitionStatus, "dismissed" | "occurred">;
      procedureDate: string;
      procedureTime: string | null;
      notes: string;
      embryosTransferred: number | null;
      cancellationDisposition?: PostProcedureCancellationDisposition;
    };

export type PostProcedureCancellationDisposition =
  "rescheduled" | "cycle_cancelled" | "not_sure";

type Props = {
  candidate: PostProcedureTransitionCandidate | null;
  visible: boolean;
  isSaving?: boolean;
  error?: string | null;
  onClose: () => void;
  onResolve: (result: PostProcedureTransitionResult) => void;
};

type DialogStep = "confirm" | "cancellation" | "details";
type ResolutionStatus = Exclude<PostProcedureTransitionStatus, "dismissed">;

const POST_PROCEDURE_ILLUSTRATION =
  require("@src/apps/moon/assets/images/calendar-reminder-dialog-illustration.png") as number;

function procedureCopyKey(procedure: PostProcedureTransitionProcedure) {
  switch (procedure) {
    case "insemination":
      return "insemination";
    case "ovulation_window":
      return "ovulation_window";
    default:
      return "transfer";
  }
}

function initialDateTime(candidate: PostProcedureTransitionCandidate | null) {
  if (!candidate) return null;
  return `${candidate.procedureDate}T${candidate.procedureTime ?? "12:00"}:00`;
}

function dateOnly(value: string | null): string {
  return value?.slice(0, 10) ?? "";
}

function timeOnly(value: string | null): string | null {
  if (!value) return null;
  const match = /T(\d{2}:\d{2})/.exec(value);
  return match?.[1] ?? null;
}

function mergeDateAndTime(
  date: string,
  time: string,
  onChange: (value: string | null) => void,
  timeOptional = false,
) {
  if (!date) {
    onChange(null);
    return;
  }
  if (timeOptional && !time) {
    onChange(date);
    return;
  }
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = (time || "12:00").split(":").map(Number);
  const next = new Date(year!, month! - 1, day!);
  const safeHours =
    typeof hours === "number" && Number.isFinite(hours) ? hours : 12;
  const safeMinutes =
    typeof minutes === "number" && Number.isFinite(minutes) ? minutes : 0;
  next.setHours(safeHours, safeMinutes, 0, 0);
  onChange(next.toISOString());
}

function InlineDateTimePicker({
  value,
  onChange,
  timeOptional = false,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  timeOptional?: boolean;
}) {
  const dateString = dateOnly(value);
  const timeString = timeOnly(value) ?? "";
  const fallbackDate = new Date();
  const fallbackDateString = `${fallbackDate.getFullYear()}-${String(
    fallbackDate.getMonth() + 1,
  ).padStart(2, "0")}-${String(fallbackDate.getDate()).padStart(2, "0")}`;

  return (
    <View style={{ flexDirection: "row", gap: 8 }}>
      <View style={{ flex: 1 }}>
        <DateField
          label=""
          value={dateString}
          onChange={(date) =>
            mergeDateAndTime(
              date,
              timeOptional ? timeString : timeString || "12:00",
              onChange,
              timeOptional,
            )
          }
        />
      </View>
      <View style={{ flex: 1 }}>
        <MoonTimeField
          label=""
          placeholder="HH:MM"
          value={timeString}
          onChange={(time) =>
            mergeDateAndTime(
              dateString || fallbackDateString,
              timeOptional ? time : time || "12:00",
              onChange,
              timeOptional,
            )
          }
        />
      </View>
    </View>
  );
}

function ChoiceRow({
  icon,
  title,
  detail,
  onPress,
  action,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
  onPress: () => void;
  /** Stable analytics identifier; the visible title is translated copy. */
  action: string;
}) {
  return (
    <ActionRowCard
      onPress={onPress}
      action={action}
      leading={icon}
      title={title}
      subtitle={detail}
      accessibilityLabel={`${title}. ${detail}`}
      style={{ marginBottom: 0 }}
    />
  );
}

export function PostProcedureTransitionDialog({
  candidate,
  visible,
  isSaving = false,
  error,
  onClose,
  onResolve,
}: Props) {
  const t = useTranslations();
  const [step, setStep] = useState<DialogStep>("confirm");
  const [resolutionStatus, setResolutionStatus] =
    useState<ResolutionStatus>("occurred");
  const [cancellationDisposition, setCancellationDisposition] =
    useState<PostProcedureCancellationDisposition | null>(null);
  const [procedureDateTime, setProcedureDateTime] = useState<string | null>(
    initialDateTime(candidate),
  );
  const [rescheduledDateTime, setRescheduledDateTime] = useState<string | null>(
    null,
  );
  const [embryosTransferred, setEmbryosTransferred] = useState("");
  const [notes, setNotes] = useState("");

  const copyKey = procedureCopyKey(candidate?.procedure ?? "embryo_transfer");
  const isTransfer = candidate?.procedure === "embryo_transfer";
  const title = t(`homepage.review_concept.post_procedure.${copyKey}.title`);
  const prompt = t(`homepage.review_concept.post_procedure.${copyKey}.prompt`);
  const dateLabel = t(
    `homepage.review_concept.post_procedure.${copyKey}.date_label`,
  );

  useEffect(() => {
    if (!visible) return;
    setStep("confirm");
    setResolutionStatus("occurred");
    setCancellationDisposition(null);
    setProcedureDateTime(initialDateTime(candidate));
    setRescheduledDateTime(null);
    setEmbryosTransferred("");
    setNotes("");
  }, [candidate, visible]);

  const procedureDate = dateOnly(procedureDateTime);
  const procedureTime = timeOnly(procedureDateTime);
  const rescheduledDate = dateOnly(rescheduledDateTime);
  const rescheduledTime = timeOnly(rescheduledDateTime);
  const isProcedureRecorded = resolutionStatus === "occurred";
  const isRescheduleDisposition = cancellationDisposition === "rescheduled";
  const canSave =
    (!isProcedureRecorded && !isRescheduleDisposition
      ? true
      : (isProcedureRecorded ? procedureDate : rescheduledDate).length ===
        10) && !isSaving;

  const handleStatus = (status: ResolutionStatus) => {
    setResolutionStatus(status);
    setStep("details");
  };

  const handleCancellationDisposition = (
    disposition: PostProcedureCancellationDisposition,
  ) => {
    setCancellationDisposition(disposition);
    if (disposition === "cycle_cancelled") {
      onResolve({
        status: "cancelled",
        cancellationDisposition: disposition,
        procedureDate: candidate?.procedureDate ?? procedureDate,
        procedureTime: candidate?.procedureTime ?? null,
        notes: "",
        embryosTransferred: null,
      });
      return;
    }
    if (disposition === "rescheduled") {
      setRescheduledDateTime(null);
    }
    setResolutionStatus(
      disposition === "rescheduled" ? "rescheduled" : "pending",
    );
    setStep("details");
  };

  const handleSave = () => {
    if (!canSave) return;
    if (!isProcedureRecorded) {
      if (isRescheduleDisposition) {
        onResolve({
          status: "rescheduled",
          cancellationDisposition: "rescheduled",
          procedureDate: rescheduledDate,
          procedureTime: rescheduledTime,
          notes: notes.trim(),
          embryosTransferred: null,
        });
        return;
      }
      onResolve({
        status: resolutionStatus,
        procedureDate: candidate?.procedureDate ?? procedureDate,
        procedureTime: candidate?.procedureTime ?? null,
        notes: notes.trim(),
        embryosTransferred: null,
      });
      return;
    }
    onResolve({
      status: "occurred",
      procedureDate,
      procedureTime,
      notes: notes.trim(),
      embryosTransferred:
        isTransfer && embryosTransferred.trim()
          ? Number(embryosTransferred)
          : null,
    });
  };

  const detailSubtitle = useMemo(
    () =>
      t(`homepage.review_concept.post_procedure.${copyKey}.details_subtitle`),
    [copyKey, t],
  );
  const resolutionCopyKey =
    resolutionStatus === "rescheduled"
      ? cancellationDisposition === "rescheduled"
        ? "rescheduled"
        : "not_yet"
      : resolutionStatus === "pending"
        ? "not_sure"
        : resolutionStatus;
  const detailTitle = isProcedureRecorded
    ? detailSubtitle
    : t(
        `homepage.review_concept.post_procedure.${resolutionCopyKey}_details_title`,
      );
  const detailPrompt = isProcedureRecorded
    ? prompt
    : t(
        `homepage.review_concept.post_procedure.${resolutionCopyKey}_details_prompt`,
      );
  const detailSaveLabel = isProcedureRecorded
    ? t("homepage.review_concept.post_procedure.save_and_continue")
    : t(`homepage.review_concept.post_procedure.${resolutionCopyKey}_save`);
  const notesPlaceholder = isProcedureRecorded
    ? t("homepage.review_concept.post_procedure.notes_placeholder")
    : t(
        `homepage.review_concept.post_procedure.${resolutionCopyKey}_notes_placeholder`,
      );
  const scheduleDateLabel = isRescheduleDisposition
    ? t("homepage.review_concept.post_procedure.rescheduled_date_label")
    : dateLabel;

  return (
    <Dialog
      visible={visible && candidate != null}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      surfaceStyle={{ maxHeight: 720 }}
      contentStyle={{ paddingHorizontal: 20, paddingBottom: 18 }}
    >
      {step !== "confirm" ? (
        <IconButton
          accessibilityLabel={t("homepage.review_concept.post_procedure.back")}
          action="homepage.review_concept.post_procedure.back"
          onPress={() => {
            setStep(
              step === "details" && cancellationDisposition
                ? "cancellation"
                : "confirm",
            );
          }}
          hitSlop={8}
          size="sm"
          surface="bare"
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 2,
          }}
        >
          <ChevronLeft
            size={20}
            color={EMBER.colors.textLight}
            strokeWidth={2}
          />
        </IconButton>
      ) : null}

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 14, paddingBottom: 2 }}
      >
        <View style={{ alignItems: "center", gap: 8, paddingTop: 4 }}>
          <DialogIcon
            source={POST_PROCEDURE_ILLUSTRATION}
            width={68}
            height={72}
            style={{ marginBottom: 2 }}
            testID="post-procedure-transition-illustration"
          />
          <DialogHeader
            align="center"
            title={
              step === "confirm"
                ? title
                : step === "cancellation"
                  ? t(
                      "homepage.review_concept.post_procedure.cancellation_title",
                    )
                  : detailTitle
            }
            subtitle={
              step === "confirm"
                ? t("homepage.review_concept.post_procedure.subtitle")
                : step === "cancellation"
                  ? t(
                      "homepage.review_concept.post_procedure.cancellation_prompt",
                    )
                  : detailPrompt
            }
            style={{ marginBottom: 0 }}
            subtitleStyle={{ textAlign: "center", width: "100%" }}
          />
        </View>

        {step === "confirm" ? (
          <View style={{ gap: 10 }}>
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.textMid,
                fontSize: 13,
                lineHeight: 19,
                textAlign: "center",
              }}
            >
              {t(`homepage.review_concept.post_procedure.${copyKey}.question`)}
            </Text>
            <ChoiceRow
              icon={<CheckCircle2 size={16} color={EMBER.colors.textMid} />}
              title={t("homepage.review_concept.post_procedure.yes_title")}
              detail={t("homepage.review_concept.post_procedure.yes_detail")}
              onPress={() => handleStatus("occurred")}
              action="homepage.review_concept.post_procedure.yes_title"
            />
            <ChoiceRow
              icon={<CalendarClock size={16} color={EMBER.colors.textMid} />}
              title={t("homepage.review_concept.post_procedure.not_yet_title")}
              detail={t(
                "homepage.review_concept.post_procedure.not_yet_detail",
              )}
              onPress={() => handleStatus("rescheduled")}
              action="homepage.review_concept.post_procedure.not_yet_title"
            />
            <ChoiceRow
              icon={<CircleAlert size={16} color={EMBER.colors.textMid} />}
              title={t(
                "homepage.review_concept.post_procedure.cancelled_title",
              )}
              detail={t(
                "homepage.review_concept.post_procedure.cancelled_detail",
              )}
              onPress={() => {
                setResolutionStatus("cancelled");
                setStep("cancellation");
              }}
              action="homepage.review_concept.post_procedure.cancelled_title"
            />
          </View>
        ) : step === "cancellation" ? (
          <View style={{ gap: 10 }}>
            <ChoiceRow
              icon={<CalendarClock size={16} color={EMBER.colors.textMid} />}
              title={t(
                "homepage.review_concept.post_procedure.rescheduled_title",
              )}
              detail={t(
                "homepage.review_concept.post_procedure.rescheduled_detail",
              )}
              onPress={() => handleCancellationDisposition("rescheduled")}
              action="homepage.review_concept.post_procedure.rescheduled_title"
            />
            <ChoiceRow
              icon={<CircleAlert size={16} color={EMBER.colors.textMid} />}
              title={t(
                "homepage.review_concept.post_procedure.cycle_cancelled_title",
              )}
              detail={t(
                "homepage.review_concept.post_procedure.cycle_cancelled_detail",
              )}
              onPress={() => handleCancellationDisposition("cycle_cancelled")}
              action="homepage.review_concept.post_procedure.cycle_cancelled_title"
            />
            <ChoiceRow
              icon={<ClipboardList size={16} color={EMBER.colors.textMid} />}
              title={t("homepage.review_concept.post_procedure.not_sure_title")}
              detail={t(
                "homepage.review_concept.post_procedure.not_sure_detail",
              )}
              onPress={() => handleCancellationDisposition("not_sure")}
              action="homepage.review_concept.post_procedure.not_sure_title"
            />
          </View>
        ) : (
          <View style={{ gap: 14 }}>
            {isProcedureRecorded || isRescheduleDisposition ? (
              <MoonFormField
                label={scheduleDateLabel}
                labelIcon={CalendarClock}
                required
              >
                <InlineDateTimePicker
                  value={
                    isProcedureRecorded
                      ? procedureDateTime
                      : rescheduledDateTime
                  }
                  onChange={
                    isProcedureRecorded
                      ? setProcedureDateTime
                      : setRescheduledDateTime
                  }
                  timeOptional={isRescheduleDisposition}
                />
              </MoonFormField>
            ) : null}
            {isProcedureRecorded && isTransfer ? (
              <TextField
                label={t(
                  "homepage.review_concept.post_procedure.embryos_label",
                )}
                labelIcon={ClipboardList}
                value={embryosTransferred}
                onChangeText={(value) =>
                  setEmbryosTransferred(value.replace(/[^0-9]/g, ""))
                }
                placeholder={t(
                  "homepage.review_concept.post_procedure.embryos_placeholder",
                )}
                keyboardType="number-pad"
              />
            ) : null}
            <TextField
              label={t("homepage.review_concept.post_procedure.notes_label")}
              labelIcon={ClipboardList}
              value={notes}
              onChangeText={setNotes}
              placeholder={notesPlaceholder}
              notes
            />
            {error ? (
              <ActionBanner variant="important" message={error} />
            ) : null}
            <PrimaryButton
              label={detailSaveLabel}
              onPress={handleSave}
              disabled={!canSave}
              loading={isSaving}
              fullWidth
            />
          </View>
        )}
      </ScrollView>
    </Dialog>
  );
}
